import { Attention, Spinner, Switch, Typography } from "@optimizely/axiom";
import { useEffect, useState } from "react";
import styles from "./GenAIPage.module.scss";
import { ConfirmationDialog } from "../../../components/ConfirmationDialog/ConfirmationDialog";
import { DisableGenAIConfirmDialog } from "../../../components/GenAI/DisableGenAIConfirmDialog";
import { emitToast } from "../../../../lib/toaster-utils";
import { useAnalyticsTracking } from "../../../../hooks/useAnalyticsTracking/useAnalyticsTracking";
import {
    ANALYTICS_EVENT_NAMES,
    ANALYTICS_FLOWS,
    ANALYTICS_TRACKED_COMPONENTS
} from "../../../../constants/analytics-constants";
import { useUserContext } from "../../../../providers/UserProvider";
import { useOrganizationSettings } from "../../../../hooks/useOrganizationSettings/useOrganizationSettings";
import { UNEXPECTED_ERROR } from "../../../../constants/constants";
import { datadogRum } from "@datadog/browser-rum";
import { OpalCompleteRemovalAlert } from "../../../components/OpalCompleteRemovalAlert/OpalCompleteRemovalAlert";
import { useFeatureFlag } from "../../../../hooks/useFeatureFlag/useFeatureFlag";
import { useOpalBranding } from "../../../../hooks/useOpalBranding/useOpalBranding";
import { Flags } from "../../../../feature-flags/flags";
import { GenAIValidation } from "../../../../rest-model/GenAIValidation";

export const GenAIPage = () => {
    const { sendTrackEvent } = useAnalyticsTracking();
    const { organizationId } = useUserContext();
    const { settings, updateGenAISettings, loading: settingsLoading } = useOrganizationSettings({ organizationId });
    const opalAutoProvisioningFlag = useFeatureFlag(Flags.ENABLE_OPAL_AUTO_PROVISIONING);
    const { opalName } = useOpalBranding();
    const [loading, setLoading] = useState(false);
    const [confirmationDialogType, setConfirmationDialogType] = useState<"off" | "on" | null>(null);
    const [error, setError] = useState("");
    const [organizationGenAIEnabled, setOrganizationGenAIEnabled] = useState(settings?.genAI?.enabled);
    const [groupsWithOpal, setGroupsWithOpal] = useState<{ productGroups: string[]; otherGroups: string[] }>({
        productGroups: [],
        otherGroups: []
    });

    useEffect(() => {
        if (settings) {
            setOrganizationGenAIEnabled(settings.genAI.enabled);
        }
    }, [settings]);

    const handleConfirmDisable = async () => {
        if (loading) return;

        setLoading(true);
        setError("");

        try {
            await updateGenAISettings({ enabled: false });

            setOrganizationGenAIEnabled(false);
            setConfirmationDialogType(null);
            setGroupsWithOpal({ productGroups: [], otherGroups: [] });
            emitToast({ message: `${opalName} settings successfully updated.` });
            sendTrackEvent({
                name: ANALYTICS_EVENT_NAMES.GEN_AI_UPDATE,
                component: ANALYTICS_TRACKED_COMPONENTS.GEN_AI_TAB,
                flow: ANALYTICS_FLOWS.SETTINGS,
                data: {
                    status: "Disabled",
                    organizationId
                }
            });
        } catch (err: any) {
            console.error("GenAI disable error:", err);
            datadogRum.addError(err);

            // If feature flag is enabled and we got validation errors, show cleanup modal
            if (opalAutoProvisioningFlag.enabled && Array.isArray(err) && err.length > 0) {
                const validation = GenAIValidation.fromErrorResponse(err);
                setGroupsWithOpal({
                    productGroups: validation.productGroupNames,
                    otherGroups: validation.otherGroupNames
                });
            } else {
                const errorMessage = err?.message || (Array.isArray(err) && err[0]?.message) || UNEXPECTED_ERROR;
                setError(errorMessage);
            }
        } finally {
            setLoading(false);
        }
    };

    const toggleGenAI = () => {
        setLoading(true);
        setError("");
        const newSetting = !organizationGenAIEnabled;
        updateGenAISettings({
            enabled: newSetting
        })
            .then(() => {
                setOrganizationGenAIEnabled(newSetting);
                setConfirmationDialogType(null);
                emitToast({ message: `${opalName} settings successfully updated.` });
                sendTrackEvent({
                    name: ANALYTICS_EVENT_NAMES.GEN_AI_UPDATE,
                    component: ANALYTICS_TRACKED_COMPONENTS.GEN_AI_TAB,
                    flow: ANALYTICS_FLOWS.SETTINGS,
                    data: {
                        status: newSetting ? "Enabled" : "Disabled",
                        organizationId
                    }
                });
            })
            .catch((err: any) => {
                console.error(err);
                datadogRum.addError(err);
                setError(err.message || UNEXPECTED_ERROR);
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const closeConfirmation = () => {
        setError("");
        setConfirmationDialogType(null);
        setGroupsWithOpal({ productGroups: [], otherGroups: [] });
    };

    const enabling = confirmationDialogType === "on";
    const confirmationText = `Are you sure you want to ${
        enabling ? "enable" : "disable"
    } ${opalName} for your entire organization?`;

    const isDisablingWithNewFlow = confirmationDialogType === "off" && opalAutoProvisioningFlag.enabled;

    return (
        <div className={"push-double content-container " + styles["gen-ai"]}>
            <Typography type="header2" className="flex justify-content-between push-triple--bottom">
                Enable {opalName}
                <div className="flex align-items-center">
                    {(loading || settingsLoading) && <Spinner />}
                    {!settingsLoading && (
                        <Switch
                            elementId={"genai-toggle"}
                            isDisabled={settingsLoading}
                            checked={organizationGenAIEnabled}
                            onClick={() => setConfirmationDialogType(organizationGenAIEnabled ? "off" : "on")}
                        />
                    )}
                </div>
            </Typography>
            {isDisablingWithNewFlow && (
                <DisableGenAIConfirmDialog
                    loading={loading}
                    error={error}
                    onCancel={closeConfirmation}
                    onConfirm={handleConfirmDisable}
                    productGroupNames={groupsWithOpal.productGroups}
                    otherGroupNames={groupsWithOpal.otherGroups}
                />
            )}
            {!!confirmationDialogType && !isDisablingWithNewFlow && (
                <ConfirmationDialog
                    onCancel={closeConfirmation}
                    onConfirmation={toggleGenAI}
                    style={enabling ? "highlight" : "danger"}
                    loading={loading}
                    title={!enabling ? `Disable ${opalName}` : `Enable ${opalName}`}
                >
                    {error && (
                        <Attention type="bad-news" className="push--bottom">
                            {error}
                        </Attention>
                    )}
                    {confirmationText}
                    {!enabling && (
                        <>
                            This will turn off {opalName} features across all Optimizely products. You can re-enable it
                            at any time or manage settings at the product instance level.
                        </>
                    )}
                </ConfirmationDialog>
            )}

            <Typography type="body">
                This setting allows you to manage the use of {opalName} features at the organizational level. By
                default, the setting is <span className="weight--bold">On&nbsp;(Opt&#8209;in)</span>.
            </Typography>
            <ul className={styles["gen-ai__list"]}>
                <li>
                    <Typography type="body">
                        <span className="weight--bold">On</span>: Enables AI-powered content and functionality across
                        all Optimizely applications and within Opti ID.This includes access to {opalName}, your AI
                        assistant, which provides a personalized, streamlined way to get work done across products.
                    </Typography>
                </li>
                <li>
                    <Typography type="body">
                        <span className="weight--bold">Off</span>: Disables AI-generated personalization and other{" "}
                        {opalName} features across all Optimizely applications and within Opti ID.
                    </Typography>
                </li>
            </ul>
            <Typography type="body">
                Adjust this setting to align with your organization's preferences for AI-driven experiences. Enabling{" "}
                {opalName} allows you to take advantage of its AI capabilities, designed to become the primary interface
                for accomplishing tasks, unifying your Optimizely products and extending seamlessly into other tools
                your teams use.
            </Typography>
            <OpalCompleteRemovalAlert genAIDisabled={!organizationGenAIEnabled} />
        </div>
    );
};

GenAIPage.displayName = "GenAIPage";
